import { FC, useEffect, useMemo, useState } from 'react';
import { Checkbox, Input, Spin, Switch } from 'antd';
import { CustomModal } from 'components/molecules/PricingSimulator/Modals/ScenarioColumnEditModal';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { sendNotification } from 'utils/helperMethods';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import { antIcon } from 'components/molecules/Filter/FilterApplyButton';
import { PromoLibraryKpiConfigType } from 'types/tradePromoOptimization';
import _ from 'lodash';
import { CustomSelect } from 'components/molecules/PricingSimulator/DimSection';
import DownSvg from 'assets/svg/Down';

type Props = {
    open: boolean;
    onSubmit: Function;
    onCancel: Function;
    data: DataType;
    updatePromoLibraryLoading: boolean;
};

export type DataType = {
    kpiObject: PromoLibraryKpiConfigType;
    rowId?: number;
    rowLabel?: string;
    value?: any[]; // if clicked on column header then pre defined value wont be present
};
const EditToolsMetricsModal: FC<Props> = ({ open, data, onSubmit, onCancel, updatePromoLibraryLoading }) => {
    const { kpiObject, value: currentValue, rowId, rowLabel } = data;
    const dynamicKpiKey = Object.keys(kpiObject || {})?.[0];
    const {
        isEditable = null,
        label = '',
        type = '',
        post = '',
        pre = '',
        storageType = '',
        allowNegative = false,
        allowedDecimals = 0,
        maxValue = null,
        minValue = null,
        allowedValues = [],
    } = kpiObject?.[dynamicKpiKey] || {};
    const [value, setValue] = useState<any[]>([]);

    function inputChangeHandler(inputValue: any, index: number) {
        const temp = [...value];
        temp[index] = inputValue;
        setValue(temp);
    }

    const memoisedAllowedValuesList = useMemo(() => {
        return allowedValues?.map((item: any) => ({ value: item, label: item }));
    }, [allowedValues]);

    //set default value
    useEffect(() => {
        if (open) {
            //set default value when column edit is enabled and type is boolean
            if (type === 'boolean' && !rowId) {
                setValue([true]);
                return;
            }
            if (type === 'text' && allowedValues?.length > 0) {
                setValue(
                    currentValue && allowedValues?.includes(currentValue?.[0]) ? currentValue : [allowedValues?.[0]],
                );
                return;
            }
            setValue(currentValue ?? []); //when clicked on cell, it will pre populate the values
        }
    }, [open]);

    const disableButton = !value?.length || value?.includes(undefined) || value?.includes(null) || value?.includes('');

    const currentValueString = _.isNil(currentValue)
        ? ''
        : type === 'boolean'
        ? _.startCase(String(currentValue?.[0]))
        : currentValue.join(' - ');

    const isRange = type === 'integer' && storageType === 'range';

    return (
        <CustomModal
            style={{ borderRadius: 8 }}
            cancelButtonProps={{ style: { borderRadius: 6 } }}
            okButtonProps={{
                disabled: disableButton,
                style: { borderRadius: 6 },
            }}
            onOk={() => {
                if (updatePromoLibraryLoading) return;

                if (type === 'integer') {
                    switch (true) {
                        //RANGE VALIDATION
                        case isRange && Number(value[1]) < Number(value[0]):
                            sendNotification('error', 'Max value should be greater than min value');
                            return;
                        //MIN VALUE VALIDATION

                        case minValue !== null && value.some((val) => Number(val) < minValue):
                            sendNotification('error', `Value cannot be less than minimum value ${minValue}`);
                            return;
                        //MAX VALUE VALIDATION
                        case maxValue !== null && value.some((val) => Number(val) > maxValue):
                            sendNotification('error', `Value cannot be greater than maximum value ${maxValue}`);
                            return;
                        //ALLOW NEGATIVE VALIDATION
                        case !allowNegative && value.some((val) => Number(val) < 0):
                            sendNotification('error', `Value cannot be negative`);
                            return;

                        //ALLOW DECIMALS VALIDATION
                        case allowedDecimals !== null &&
                            value.some((val) => {
                                const decimalPart = val.toString().split('.')[1];
                                return decimalPart && decimalPart.length > allowedDecimals;
                            }):
                            sendNotification('error', `Value cannot have decimal places more than ${allowedDecimals}`);
                            return;
                    }
                }

                const payload = {
                    kpi: {
                        key: dynamicKpiKey,
                        storage_type: storageType,
                    },
                    row_id: rowId,
                    values: ['integer'].includes(type) ? value?.map((item: any) => Number(item)) : value,
                };
                onSubmit(payload);
            }}
            okText={updatePromoLibraryLoading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : 'Apply'}
            destroyOnClose
            centered
            open={open}
            width={420}
            onCancel={(e) => {
                if (updatePromoLibraryLoading) return;
                onCancel(e);
            }}
            title={
                <p className="font-sm">
                    Edit Metric: {label}
                    <span className="text-fontGreyAlt font-xs">{rowId ? ' (Cell)' : ' (Column)'}</span>
                </p>
            }
        >
            <div className="rounded border border-backgroundGreyAlt6 p-4">
                {/* CURRENT VALUE */}
                {!_.isNil(currentValue) ? (
                    <div className="font-xs flex items-center gap-3  text-fontGreyAlt mb-4">
                        <span className=" text-right w-[100px]">Current Value : </span>
                        <div className="flex gap-1 items-center">
                            {pre && <span> {pre}</span>}
                            <span> {currentValueString}</span>
                            <span> {post}</span>
                        </div>
                    </div>
                ) : null}
                {/* RANGE */}
                {isRange ? (
                    <div className="flex gap-5 items-center font-xs">
                        <div className="flex items-center  gap-2">
                            <span className=""> Min : </span>
                            <div className="flex items-center gap-1">
                                {pre}
                                <Input
                                    value={value?.[0]}
                                    onChange={(val) => {
                                        inputChangeHandler(val.target.value, 0);
                                    }}
                                    style={{ width: 100 }}
                                    type="number"
                                />
                                {post}
                            </div>
                        </div>

                        <div className="flex items-center  gap-2">
                            <span className=""> Max : </span>
                            <div className="flex items-center gap-1">
                                {pre}
                                <Input
                                    value={value?.[1]}
                                    onChange={(val) => {
                                        inputChangeHandler(val.target.value, 1);
                                    }}
                                    style={{ width: 100 }}
                                    type="number"
                                />
                                {post}
                            </div>
                        </div>
                    </div>
                ) : // NUMBER
                type === 'integer' ? (
                    <div className="flex items-center font-xs gap-2">
                        <span className="text-right w-[100px]"> New Value : </span>
                        <div className="flex items-center gap-1">
                            {pre}
                            <Input
                                value={value?.[0]}
                                onChange={(val) => {
                                    inputChangeHandler(val.target.value, 0);
                                }}
                                style={{ width: 150 }}
                                type="number"
                            />
                            {post}
                        </div>
                    </div>
                ) : // BOOLEAN
                type === 'boolean' ? (
                    <div className="flex items-center font-xs gap-3">
                        <span className="text-right w-[100px]"> New Value : </span>
                        <p className="flex items-center gap-2 font-xs ">
                            <span>False</span>
                            <Switch
                                checked={value?.[0]}
                                onChange={(val) => {
                                    setValue([val]);
                                }}
                            />
                            <span>True</span>
                        </p>
                    </div>
                ) : type === 'text' && allowedValues?.length > 0 ? (
                    <div className="flex items-center font-xs gap-3">
                        <span className="text-right w-[100px]"> New Value : </span>
                        <CustomSelect
                            showSearch={false}
                            placeholder="Select Preset"
                            className="dropdown-height w-[200px] uniqSelLoadPresetDropdown"
                            suffixIcon={<DownSvg />}
                            loading={false}
                            value={value?.[0]}
                            disabled={false}
                            options={memoisedAllowedValuesList}
                            onChange={(val: any, item: any) => {
                                setValue([val]);
                            }}
                        />
                    </div>
                ) : null}
            </div>
        </CustomModal>
    );
};

export default EditToolsMetricsModal;
